import * as React from 'react'
import {
  Edit,
  NumberInput,
  SimpleForm,
  useDataProvider,
  useRecordContext,
} from 'react-admin'
import { useWatch } from 'react-hook-form'
import { Box, Paper, Typography } from '@mui/material'

// How much contract text each extraction step reads, for this organisation.
// See docs/contracts/CONTRACT_TEXT_SELECTION.md.
const FIELDS = [
  {
    source: 'classification_chars',
    label: 'Classification',
    help: 'Broad category, contract type, supplier and effective date. Read from the start of the contract.',
  },
  {
    source: 'precedence_chars',
    label: 'Order of precedence',
    help: 'Which document governs when several are supplied.',
  },
  {
    source: 'taxonomy_chars',
    label: 'Spend taxonomy',
    help: 'Placing the contract in the spend category tree (three AI calls).',
  },
  {
    source: 'prompt_chars',
    label: 'All other prompts',
    help: 'The specialist prompts, reconciliation and summary (16 AI calls). The biggest effect on cost.',
  },
  {
    source: 'reconciliation_chars',
    label: 'Reconciliation input',
    help: "Earlier prompts' answers given to reconciliation and the summary.",
  },
]

type Estimate = {
  model: string
  calls: number
  input_tokens: number
  output_tokens: number
  cost_usd: number | null
  prices_as_of: string | null
}

// Per-contract costs are fractions of a cent, so the main figure is per 1,000 contracts
const formatCost = (estimate?: Estimate) => {
  if (!estimate) return '…'
  if (estimate.cost_usd === null) return 'price not known for this model'
  const perThousand = (estimate.cost_usd * 1000).toFixed(2)
  return `$${perThousand} per 1,000 contracts ($${estimate.cost_usd.toFixed(
    5
  )} each)`
}

const EstimatePanel = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const values = useWatch({ name: FIELDS.map((field) => field.source) })
  const [estimate, setEstimate] = React.useState<{
    estimate_long_contract?: Estimate
    estimate_10k_contract?: Estimate
  }>({})

  React.useEffect(() => {
    const payload = Object.fromEntries(
      FIELDS.map((field, index) => [field.source, values?.[index]])
    )
    const timer = window.setTimeout(() => {
      dataProvider
        .estimateContractExtractionSettings(payload)
        .then(setEstimate)
        .catch(() => setEstimate({}))
    }, 300)
    return () => window.clearTimeout(timer)
  }, [dataProvider, values])

  const long = estimate.estimate_long_contract || record?.estimate_long_contract
  const short = estimate.estimate_10k_contract || record?.estimate_10k_contract
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, maxWidth: 640 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Projected cost per contract
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Long contract (fills every limit): <strong>{formatCost(long)}</strong>
        {long ? ` (${long.input_tokens.toLocaleString()} input tokens)` : ''}
      </Typography>
      <Typography variant="body2">
        10,000-character contract: <strong>{formatCost(short)}</strong>
        {short ? ` (${short.input_tokens.toLocaleString()} input tokens)` : ''}
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: 'block', mt: 1 }}
      >
        {long
          ? `Model ${long.model}, ${long.calls} AI calls per contract.`
          : ''}{' '}
        {long?.prices_as_of ? `Prices as of ${long.prices_as_of}.` : ''} An
        estimate: about 4 characters per token and about 1,000 output tokens per
        call.
      </Typography>
    </Paper>
  )
}

const Intro = () => (
  <Box sx={{ maxWidth: 640, mb: 1 }}>
    <Typography variant="body2">
      Each step sends the AI the first part of the contract, up to the number of
      characters below. Raising a limit lets the AI read further into long
      contracts, and costs more for every contract this organisation processes.
      Changes apply to contracts processed from now on.
    </Typography>
  </Box>
)

const SettingsForm = () => {
  const record = useRecordContext()
  return (
    <SimpleForm>
      <Intro />
      {FIELDS.map((field) => (
        <NumberInput
          key={field.source}
          source={field.source}
          label={`${field.label} (characters)`}
          helperText={`${field.help} Default ${
            record?.defaults?.[field.source]?.toLocaleString() ?? ''
          }.`}
          min={1000}
          max={1000000}
          step={1000}
          sx={{ minWidth: 320 }}
        />
      ))}
      <EstimatePanel />
    </SimpleForm>
  )
}

export const ContractExtractionSettingsPage = () => (
  <Edit
    resource="contract-extraction-settings"
    id="current"
    redirect={false}
    mutationMode="pessimistic"
    title="Contract Extraction Settings"
  >
    <SettingsForm />
  </Edit>
)
